// Migrations page — run pending TenantDbContext migrations across the region's
// tenant databases. Tenant DBs are only auto-migrated at create time, so a new
// migration must be rolled out to existing tenants explicitly; this UI drives the
// admin runner:
//   POST /api/admin/tenants/{id}/migrations/run   (one tenant)
//   POST /api/admin/tenants/migrations/run-all    (every tenant in the region)
// Each returns a TenantMigrationResult reporting migrated / up_to_date / failed.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import type { GridCellProps } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import type { TenantDto, TenantMigrationResult } from '@shared/api-types';
import { adminApiFetch } from '../../lib/adminApiClient';
import { useRegion } from '../../lib/regionContext';
import { useElementWidth, toResponsiveWidth } from '@shared/useElementWidth';

const STATUS_LABEL: Record<TenantMigrationResult['status'], string> = {
  migrated: 'Migrated',
  up_to_date: 'Up to date',
  not_found: 'Not found',
  failed: 'Failed',
};

interface Toast {
  text: string;
  style: 'success' | 'warning' | 'error';
}

export function Migrations() {
  const { region } = useRegion();
  const [tenants, setTenants] = useState<TenantDto[] | null>(null);
  // Container-measured column shedding, matching the customer app's grids: the
  // grid's own width, not the viewport's, because the drawer rail changes what
  // is available by ~200px. See DESIGN.md ("Responsive").
  const [gridRef, gridWidth] = useElementWidth();
  const w = toResponsiveWidth(gridWidth);
  const [results, setResults] = useState<Record<string, TenantMigrationResult>>({});
  const [busyId, setBusyId] = useState<string | null>(null); // tenant id, or '*' for run-all
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  // Load the region's tenants once on mount. Region switching signs the admin out
  // and re-routes through /login (see RegionPicker), so this page always remounts
  // for a new region — no in-place refresh needed. The async work runs in an IIFE
  // (state is set after the await, behind a cancelled guard) to avoid a synchronous
  // setState in the effect body.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await adminApiFetch<TenantDto[]>('/api/admin/tenants');
        if (!cancelled) setTenants(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load tenants.');
          setTenants([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const runOne = useCallback(async (id: string) => {
    setBusyId(id);
    setToast(null);
    try {
      const result = await adminApiFetch<TenantMigrationResult>(
        `/api/admin/tenants/${id}/migrations/run`,
        { method: 'POST' },
      );
      setResults((prev) => ({ ...prev, [id]: result }));
      setToast(toastForOne(result));
    } catch (err) {
      setToast({ text: err instanceof Error ? err.message : 'Migration run failed.', style: 'error' });
    } finally {
      setBusyId(null);
    }
  }, []);

  const runAll = useCallback(async () => {
    setBusyId('*');
    setToast(null);
    try {
      const all = await adminApiFetch<TenantMigrationResult[]>(
        '/api/admin/tenants/migrations/run-all',
        { method: 'POST' },
      );
      setResults((prev) => {
        const next = { ...prev };
        for (const r of all) next[r.tenantId] = r;
        return next;
      });
      const migrated = all.filter((r) => r.status === 'migrated').length;
      const upToDate = all.filter((r) => r.status === 'up_to_date').length;
      const failed = all.filter((r) => r.status === 'failed').length;
      const notFound = all.filter((r) => r.status === 'not_found').length;
      setToast({
        text: `Ran ${all.length} tenant(s): ${migrated} migrated, ${upToDate} up to date, ${failed} failed${notFound > 0 ? `, ${notFound} not found` : ''}.`,
        style: failed > 0 || notFound > 0 ? 'warning' : 'success',
      });
    } catch (err) {
      setToast({ text: err instanceof Error ? err.message : 'Run-all failed.', style: 'error' });
    } finally {
      setBusyId(null);
    }
  }, []);

  // Merge tenants with their latest run result for the grid.
  const rows = useMemo(
    () =>
      (tenants ?? []).map((t) => {
        const r = results[t.id];
        return {
          id: t.id,
          name: t.name,
          tenantDatabaseName: t.tenantDatabaseName,
          statusLabel: r ? STATUS_LABEL[r.status] : '—',
          applied: r ? (r.appliedMigrations.length > 0 ? r.appliedMigrations.join(', ') : '—') : '—',
          resultError: r?.error ?? '',
        };
      }),
    [tenants, results],
  );

  const RunCell = (props: GridCellProps) => {
    const id = (props.dataItem as { id: string }).id;
    return (
      <td>
        <Button size="small" disabled={busyId !== null} onClick={() => void runOne(id)} data-testid={`run-${id}`}>
          {busyId === id ? 'Running…' : 'Run'}
        </Button>
      </td>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>Migrations</h2>
          <div style={{ color: '#64748b', fontSize: 13 }}>
            Apply pending tenant-database migrations. Region: {region.toUpperCase()}
          </div>
        </div>
        <Button
          themeColor="primary"
          disabled={busyId !== null || (tenants?.length ?? 0) === 0}
          onClick={() => void runAll()}
          data-testid="run-all-migrations"
        >
          {busyId === '*' ? 'Running…' : 'Run pending migrations (all tenants)'}
        </Button>
      </div>

      {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}

      <div ref={gridRef}>
      <Grid data={rows} style={{ height: 'calc(100dvh - 200px)' }}>
        {/* The database name is derivable from the tenant and is the widest
            column here; the applied-migrations list is detail. Tenant, last
            result, any error, and the run action all stay — those are what the
            page exists to show. */}
        <GridColumn field="name" title="Tenant" />
        {w >= 900 && <GridColumn field="tenantDatabaseName" title="Database" width="240px" />}
        <GridColumn field="statusLabel" title="Last result" width="130px" />
        {w >= 720 && <GridColumn field="applied" title="Applied migrations" />}
        <GridColumn field="resultError" title="Error" />
        <GridColumn title="Actions" width="110px" cells={{ data: RunCell }} />
      </Grid>
      </div>

      <NotificationGroup style={{ position: 'fixed', right: 16, bottom: 16 }}>
        {toast && (
          <Notification type={{ style: toast.style, icon: true }} closable onClose={() => setToast(null)}>
            <span>{toast.text}</span>
          </Notification>
        )}
      </NotificationGroup>
    </div>
  );
}

function toastForOne(r: TenantMigrationResult): Toast {
  switch (r.status) {
    case 'migrated':
      return { text: `${r.tenantDatabaseName}: applied ${r.appliedMigrations.length} migration(s).`, style: 'success' };
    case 'up_to_date':
      return { text: `${r.tenantDatabaseName}: already up to date.`, style: 'success' };
    case 'failed':
      return { text: `${r.tenantDatabaseName}: failed — ${r.error ?? 'unknown error'}.`, style: 'error' };
    default:
      return { text: 'Tenant not found in this region.', style: 'warning' };
  }
}
